import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CheckboxInput from 'app/scripts/components/common/inputs/checkbox-input';
import * as colors from 'styled-ui-kit/colors';
import { isTOL } from 'app/scripts/utils/theme-module';
import { autobind } from 'core-decorators';

const borderBottom = `1px solid ${colors.foxtrotGreyColor}`;

const Wrapper = styled.div`
  font-family: 'Open Sans', Helvetica, Arial, sans-serif;
`;

const Title = styled.div`
  text-transform: uppercase;
  border-bottom: ${borderBottom};
  color: #a8abae;
  font-size: 11px;
  padding: 8px 11px;
`;

const RoleWrapper = styled.div`
  padding: 7px 9px 10px;
  border-bottom: ${borderBottom};

  .field {
    margin-top: 0px;

    label.text {
      font-weight: bold;
    }
  }
`;

const RoleDescription = styled.div`
  font-size: 11px;
  color: #7b7f83;
  margin-left: 26px;
`;

const ApplyButtonWrapper = styled.div`
  padding: 10px;
  border-bottom: ${borderBottom};
`;

const RemoveButtonWrapper = styled.div`
  padding: 10px;
  border-bottom: ${borderBottom};
`;

const OWNER = 0;
const MODERATOR = 1;
const JOURNALIST = 2;
const MODERATION_VIEWER = 3;
const COMMUNITY_MODERATOR = 4;

const Roles = [];

Roles[OWNER] = {
  label: 'Admin',
  description: "Access to all the system's sections"
};

Roles[MODERATOR] = {
  label: 'Moderator',
  description: 'Can only access moderation sections'
};

Roles[JOURNALIST] = {
  label: 'Journalist',
  description: 'No Admin panel access, can select top comments'
};

Roles[MODERATION_VIEWER] = {
  label: 'Viewer',
  description: 'Moderation section, view permissions only'
};

Roles[COMMUNITY_MODERATOR] = {
  label: 'Community Moderator',
  description: 'No Admin Panel access, can moderate comments within the conversation'
};

export default class RoleSelector extends PureComponent {
  constructor(props) {
    super(props);

    let checkedRoles = [];
    const userRoles = props.user.roles || [];
    checkedRoles[OWNER] = props.user.type === 'admin';
    checkedRoles[MODERATOR] = props.user.type === 'moderator';
    checkedRoles[JOURNALIST] = props.user.type === 'journalist' || userRoles.includes('journalist');
    checkedRoles[MODERATION_VIEWER] =
      props.user.type === 'moderation_viewer' || userRoles.includes('moderation_viewer');
    checkedRoles[COMMUNITY_MODERATOR] =
      props.user.type === 'community_moderator' || userRoles.includes('community_moderator');

    this.state = {
      checkedRoles,
      disabledRoles: Roles.map(() => false)
    };
  }

  componentDidMount() {
    this.updateDisabledRoles();
  }

  onCheckboxChange(index) {
    this.setState(
      {
        checkedRoles: this.state.checkedRoles.map((role, currentIndex) => {
          if (currentIndex === index) {
            return !role;
          } else {
            return role;
          }
        })
      },
      () => {
        this.updateDisabledRoles();
      }
    );
  }

  // This function maps between the state of the roles checked value and
  // wether or not they're disabled.
  // `disabledRoles` and `checkedRoles` arrays share the same indices.
  //
  updateDisabledRoles() {
    const { checkedRoles } = this.state;
    let disabledRoles;
    if (checkedRoles[OWNER]) {
      disabledRoles = [false, true, true, true, true];
    } else if (checkedRoles[MODERATOR]) {
      disabledRoles = [true, false, false, true, true];
    } else if (checkedRoles[JOURNALIST] || checkedRoles[MODERATION_VIEWER]) {
      if (checkedRoles[MODERATOR]) {
        disabledRoles = [true, false, false, true, true];
      } else {
        disabledRoles = [true, true, false, false, true];
      }
    } else if (checkedRoles[COMMUNITY_MODERATOR]) {
      disabledRoles = [true, true, true, true, false];
    } else {
      disabledRoles = [false, false, false, false, false];
    }

    this.setState({ disabledRoles });
  }

  renderRoleLine(role, index) {
    const roleLine = (
      <RoleWrapper key={index}>
        <CheckboxInput
          value={this.state.checkedRoles[index]}
          disabled={this.state.disabledRoles[index]}
          text={role.label}
          onChange={this.onCheckboxChange.bind(this, index)}
        />
        <RoleDescription>{role.description}</RoleDescription>
      </RoleWrapper>
    );

    return roleLine;
  }

  @autobind
  handleApply() {
    const { checkedRoles } = this.state;
    this.props.onChange({
      admin: checkedRoles[OWNER],
      moderator: checkedRoles[MODERATOR],
      journalist: checkedRoles[JOURNALIST],
      'moderation-viewer': checkedRoles[MODERATION_VIEWER],
      'community-moderator': checkedRoles[COMMUNITY_MODERATOR]
    });
  }

  @autobind
  handleRemoveFromTeam() {
    this.props.onRemoveFromTeam();
  }

  render() {
    return (
      <Wrapper>
        <Title>Select Role:</Title>
        {Roles.map((role, index) => {
          return this.renderRoleLine(role, index);
        })}
        <ApplyButtonWrapper>
          <button className="small-green-button" onClick={this.handleApply}>
            Apply
          </button>
        </ApplyButtonWrapper>
        <RemoveButtonWrapper>
          <button className="small-transparent-button" onClick={this.handleRemoveFromTeam}>
            Remove from team
          </button>
        </RemoveButtonWrapper>
      </Wrapper>
    );
  }
}

RoleSelector.propTypes = {
  user: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemoveFromTeam: PropTypes.func.isRequired
};

RoleSelector.displayName = 'RoleSelector';
